import { useEffect, useMemo, useRef, useState } from "react";
import { IoNotifications } from "react-icons/io5";
import {
  FiBox,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiFileText,
  FiGrid,
  FiLayers,
  FiLogOut,

  FiRefreshCw,
  FiSettings,
  FiShoppingBag,
  FiShoppingCart,
  FiTag,
  FiTool,
  FiTruck,
  FiUser,
  FiUsers,
  FiMessageSquare,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Spinner, Table } from "reactstrap";
import { searchDistributorProducts } from "../../api/edi.api";
import navbarLogo from "../../assets/imgs/techmicro-logo.png";
import { logoutUser } from "../../firebase/utility";
import { setUser } from "../../store/actions/actions";
import { syncPermissions } from "../../utils/permissions";

// ──────────────────────────────────────────────
// Navigation structure
// ──────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", path: "/admin/index", icon: FiGrid },
    ],
  },
  {
    label: "Orders",
    items: [
      { name: "Sale Orders", path: "/admin/sale-orders", icon: FiShoppingBag },
      { name: "Purchase Orders", path: "/admin/purchase-orders", icon: FiShoppingCart },
      { name: "Return Orders", path: "/admin/return-orders", icon: FiRefreshCw },
      { name: "Quotes", path: "/admin/quotes", icon: FiFileText },
      { name: "Tracking", path: "/admin/shipping-tracking", icon: FiTruck },
    ],
  },
  {
    label: "Catalog",
    items: [
      { name: "Products", path: "/admin/items", icon: FiBox },
      { name: "Manufacturers", path: "/admin/manufacturers", icon: FiTool },
      { name: "Reviews", path: "/admin/reviews", icon: FiMessageSquare },
    ],
  },
  {
    label: "People",
    items: [
      { name: "Customers", path: "/admin/customers", icon: FiUsers },
      { name: "Vendors", path: "/admin/vendors", icon: FiTruck },
    ],
  },
  {
    label: "Admin",
    items: [
      { name: "Settings", path: "/admin/settings", icon: FiSettings },
      { name: "Categories", path: "/admin/categories", icon: FiLayers },
      { name: "Sub Categories", path: "/admin/sub-categories", icon: FiTag },
      { name: "Product Variant/Groups", path: "/admin/product-groups", icon: FiLayers },
      { name: "Allowed Manufacturers", path: "/admin/allowed-manufacturers", icon: FiTool },
      { name: "DNH Jobs", path: "/admin/dnh-jobs", icon: FiTool },
      { name: "Profile", path: "/admin/user-profile-page", icon: FiUser },
    ],
  },
];

// ──────────────────────────────────────────────
// Sidebar Component
// ──────────────────────────────────────────────
const Sidebar = ({ collapsed, setCollapsed }) => {
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { notifications } = useSelector((state) => state.redux);
  const dispatch = useDispatch();
  const history = useHistory();

  const unreadCount = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return 0;
    const isAdmin = user?.role === "Admin" || user?.role === "Super-admin";
    const filtered = isAdmin
      ? notifications.filter((i) => i?.type === "user" || i?.targetRole === "admin" || i?.targetRole === "all" || !i?.type)
      : notifications.filter((i) => (i?.type === "admin" && i?.userId === user?._id) || i?.userId === user?._id);
    return filtered.filter((i) => !i.read).length;
  }, [notifications, user]);


  const handleLogout = () => {
    logoutUser().then((res) => {
      if (res) {
        dispatch(setUser({}));
        history.push("/login");
      }
    });
  };

  const isActive = (path) => pathname.startsWith(path);

  return (
    <aside
      className="admin-sidebar"
      style={{ width: collapsed ? "72px" : "240px" }}
    >
      {/* ── Logo ── */}
      <div className="sidebar-logo">
        {!collapsed && (
          <img src={navbarLogo} alt="TechMicro" height={36} style={{ objectFit: "contain" }} />
        )}
        <button
          className="sidebar-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
        </button>
      </div>

      {/* ── Nav Groups ── */}
      <nav className="sidebar-nav">
        {NAV_GROUPS.map((group) => {
          const currentUserRole = user?.role?.toLowerCase();
          const syncedPermissions = syncPermissions(user?.permissions);

          const filteredItems = group.items.filter((item) => {
            if (currentUserRole === 'super-admin' || item.name === 'Profile') return true;
            const permission = syncedPermissions.find(p => p.name === item.name);
            return permission ? permission.value : false;
          });

          if (filteredItems.length === 0) return null;

          return (
            <div key={group.label} className="nav-group">
              {!collapsed && (
                <span className="nav-group-label">{group.label}</span>
              )}
              {filteredItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${active ? "active" : ""}`}
                    title={collapsed ? item.name : undefined}
                  >
                    <span className="nav-icon">
                      <Icon size={18} />
                    </span>
                    {!collapsed && <span className="nav-label">{item.name}</span>}
                    {active && !collapsed && <span className="nav-active-dot" />}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* ── Bottom Actions ── */}
      <div className="sidebar-footer">
        {/* Notifications */}
        <button
          className="sidebar-footer-btn position-relative"
          onClick={() => history.push("/admin/notifications")}
          title={unreadCount > 0 ? `${unreadCount} Unread Notifications` : "Notifications"}
        >
          <span className="nav-icon" style={{ position: "relative" }}>
            <IoNotifications size={18} />
            {unreadCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#ef4444",
                  boxShadow: "0 0 0 2px #fff",
                }}
              />
            )}
          </span>
          {!collapsed && (
            <span className="nav-label d-flex align-items-center justify-content-between flex-grow-1">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <span
                  className="badge rounded-pill bg-danger ms-auto"
                  style={{ fontSize: "10px", padding: "2px 6px" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </span>
          )}
        </button>

        {/* User info + logout */}
        <div className="sidebar-user">
          <div className="sidebar-avatar">
            {user?.username?.[0]?.toUpperCase() ?? "U"}
          </div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <span className="sidebar-username">{user?.username ?? "Admin"}</span>
              <span className="sidebar-role">{user?.role ?? "Staff"}</span>
            </div>
          )}
          <button
            className="sidebar-logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            <FiLogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
